import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  BarChart3, RefreshCw, Download, Search, X, Loader2,
  TrendingUp, DollarSign, Eye, MousePointerClick, Target,
  ChevronUp, ChevronDown, Megaphone, Info, Link2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/Admin/Layout/AdminLayout';
import apiClient from '../../services/api/client';
import { ENDPOINTS } from '../../services/api/endpoints';
import { useSeller } from '../../context/SellerContext';
import MarketplaceSelector from '../../components/shared/MarketplaceSelector';
import AdsConnectModal from '../../components/Admin/SellerAccounts/AdsConnectModal';
// Phase 1: sample data until the Amazon Advertising API sync (Phase 2) populates the ad_* tables.
import { MOCK_CAMPAIGNS, MOCK_KEYWORDS, mockStats } from './adPerformanceMockData';

// ─── Config ──────────────────────────────────────────────────────────────────

const AD_TYPES = {
  SPONSORED_PRODUCTS: { label: 'Sponsored Products', short: 'SP', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  SPONSORED_BRANDS: { label: 'Sponsored Brands', short: 'SB', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  SPONSORED_DISPLAY: { label: 'Sponsored Display', short: 'SD', color: 'text-violet-700', bg: 'bg-violet-50', border: 'border-violet-200' },
};

const STATE_BADGE = {
  ENABLED: { label: 'Enabled', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  PAUSED: { label: 'Paused', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
  ARCHIVED: { label: 'Archived', color: 'text-slate-600', bg: 'bg-slate-50', border: 'border-slate-200' },
};

const DATE_PRESETS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '', label: 'All time' },
];

const PAGE_SIZES = [25, 50, 100];

const selectCls =
  'px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatPrice(val) {
  if (val == null) return '—';
  return `₹${Number(val).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNum(val) {
  if (val == null) return '—';
  return Number(val).toLocaleString('en-IN');
}

function formatPct(val) {
  if (val == null || val === '') return '—';
  return `${Number(val).toFixed(2)}%`;
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - Number(days));
  return d.toISOString().split('T')[0];
}

// ─── Badges ──────────────────────────────────────────────────────────────────

function AdTypeBadge({ adType }) {
  const t = AD_TYPES[adType];
  if (!t) return <span className="text-xs text-gray-400">{adType || '—'}</span>;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${t.bg} ${t.color} ${t.border} border`} title={t.label}>
      {t.short}
    </span>
  );
}

function StateBadge({ state }) {
  const s = STATE_BADGE[state] || STATE_BADGE.ARCHIVED;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold ${s.bg} ${s.color} ${s.border} border`}>
      {s.label}
    </span>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function AdPerformancePage() {
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || '';
  const sellerChannels = useMemo(
    () => (sellerCtx?.channels || []).filter((c) => c.accountStatus === 'AUTHORIZED'),
    [sellerCtx]
  );

  const [tab, setTab] = useState('campaigns'); // 'campaigns' | 'keywords'
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [datePreset, setDatePreset] = useState('30');

  // Filters
  const [search, setSearch] = useState('');
  const [filterAdType, setFilterAdType] = useState('');
  const [filterState, setFilterState] = useState('');
  const [filterTargetType, setFilterTargetType] = useState('');

  // Data
  const [campaigns, setCampaigns] = useState([]);
  const [keywords, setKeywords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [isDemo, setIsDemo] = useState(false);
  const [adsModalOpen, setAdsModalOpen] = useState(false);

  // Sort + pagination
  const [sortBy, setSortBy] = useState('cost');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);

  const dateParams = useMemo(() => {
    if (!datePreset) return {};
    return { dateFrom: isoDaysAgo(datePreset), dateTo: new Date().toISOString().split('T')[0] };
  }, [datePreset]);

  const buildParams = useCallback(
    (extra = {}) => {
      const params = new URLSearchParams();
      if (sellerId) params.append('sellerId', sellerId);
      if (selectedAccountId) params.append('sellerAccountId', selectedAccountId);
      if (dateParams.dateFrom) params.append('dateFrom', dateParams.dateFrom);
      if (dateParams.dateTo) params.append('dateTo', dateParams.dateTo);
      Object.entries(extra).forEach(([k, v]) => v && params.append(k, v));
      return params.toString();
    },
    [sellerId, selectedAccountId, dateParams]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [campRes, statsRes, kwRes] = await Promise.all([
        apiClient.get(`${ENDPOINTS.AD_PERFORMANCE.CAMPAIGNS}?${buildParams({ adType: filterAdType, state: filterState })}`),
        apiClient.get(`${ENDPOINTS.AD_PERFORMANCE.STATS}?${buildParams({ adType: filterAdType, state: filterState })}`),
        apiClient.get(`${ENDPOINTS.AD_PERFORMANCE.KEYWORDS}?${buildParams({ targetType: filterTargetType })}`),
      ]);

      // apiClient unwraps to the body already, but stay tolerant of both shapes.
      const asArray = (res) =>
        Array.isArray(res) ? res
        : Array.isArray(res?.data) ? res.data
        : Array.isArray(res?.content) ? res.content
        : Array.isArray(res?.data?.content) ? res.data.content
        : [];

      const asObj = (res) => res?.data ?? res ?? null;

      const camp = asArray(campRes);
      const kw   = asArray(kwRes);

      console.log("campaigns parsed:", camp.length);

      setIsDemo(false);
      setCampaigns(camp);
      setKeywords(kw);
      setStats(asObj(statsRes));
    } catch (err) {
      console.error("ad-performance load failed", err);
      setIsDemo(false);
      setCampaigns([]);
      setKeywords([]);
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams, filterAdType, filterState, filterTargetType]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setPage(0);
  }, [tab, search, filterAdType, filterState, filterTargetType, datePreset]);

  const handleSync = useCallback(async () => {
    if (sellerChannels.length === 0) {
      toast.error('No authorized marketplace connected');
      return;
    }
    setSyncing(true);
    try {
      // Phase 1: backend returns 501 until the Amazon Advertising API integration lands.
      await apiClient.post(`${ENDPOINTS.AD_PERFORMANCE.SYNC}?sellerAccountId=${selectedAccountId || sellerChannels[0].id}`);
      await loadData();
    } catch (err) {
      const msg = err?.response?.data?.message;
      if (err?.response?.status === 501) {
        toast(msg || 'Ad sync needs the Amazon Advertising API (coming soon)', { icon: 'ℹ️', duration: 4000 });
      } else {
        toast.error(msg || 'Sync failed');
      }
    } finally {
      setSyncing(false);
    }
  }, [sellerChannels, selectedAccountId, loadData]);

  const handleSort = (field) => {
    if (sortBy === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortBy(field);
      setSortDir('desc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronDown size={13} className="text-gray-300" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={13} className="text-[#C45500]" />
    ) : (
      <ChevronDown size={13} className="text-[#C45500]" />
    );
  };

  // Active dataset (client-side search + sort + paginate)
  const rows = tab === 'campaigns' ? campaigns : keywords;

  const filteredRows = useMemo(() => {
    let r = rows;
    if (tab === 'campaigns') {
      if (filterAdType) r = r.filter((x) => x.adType === filterAdType);
      if (filterState) r = r.filter((x) => x.state === filterState);
    } else if (filterTargetType) {
      r = r.filter((x) => x.targetType === filterTargetType);
    }
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((row) =>
        tab === 'campaigns'
          ? (row.name || '').toLowerCase().includes(q) || (row.campaignId || '').toLowerCase().includes(q)
          : (row.keywordText || '').toLowerCase().includes(q) ||
            (row.expression || '').toLowerCase().includes(q) ||
            (row.campaignName || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...r].sort((a, b) => {
      const av = a[sortBy], bv = b[sortBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' || !isNaN(Number(av)) ? Number(av) - Number(bv) : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [rows, search, sortBy, sortDir, tab, filterAdType, filterState, filterTargetType]);

  const totalElements = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalElements / size));
  const pageRows = filteredRows.slice(page * size, page * size + size);
  const startRecord = totalElements === 0 ? 0 : page * size + 1;
  const endRecord = Math.min((page + 1) * size, totalElements);

  const hasFilters = search || filterAdType || filterState || filterTargetType;

  const exportCsv = () => {
    const headers =
      tab === 'campaigns'
        ? ['Campaign', 'Type', 'State', 'Spend', 'Sales', 'Impressions', 'Clicks', 'Orders', 'CTR%', 'ACOS%', 'ROAS']
        : ['Keyword/Target', 'Type', 'Match', 'Campaign', 'Spend', 'Sales', 'Impressions', 'Clicks', 'Orders', 'ACOS%', 'ROAS'];
    const lines = filteredRows.map((r) =>
      tab === 'campaigns'
        ? [r.name, r.adType, r.state, r.cost, r.sales, r.impressions, r.clicks, r.orders, r.ctr, r.acos, r.roas]
        : [r.keywordText || r.expression, r.targetType, r.matchType, r.campaignName, r.cost, r.sales, r.impressions, r.clicks, r.orders, r.acos, r.roas]
    );
    const csv = [headers.join(','), ...lines.map((l) => l.map((c) => `"${c ?? ''}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ad-performance-${tab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─── Stat cards ──────────────────────────────────────────────────────────────
  const statCards = [
    { label: 'Total Spend', value: stats ? formatPrice(stats.totalSpend) : '—', icon: DollarSign, color: 'bg-[#f08804]' },
    { label: 'Sales from Ads', value: stats ? formatPrice(stats.totalSales) : '—', icon: TrendingUp, color: 'bg-emerald-500' },
    { label: 'ACOS', value: stats ? formatPct(stats.acos) : '—', icon: Target, color: 'bg-[#232F3E]' },
    { label: 'ROAS', value: stats?.roas != null ? `${Number(stats.roas).toFixed(2)}x` : '—', icon: BarChart3, color: 'bg-violet-500' },
  ];

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Megaphone size={20} className="text-[#f08804]" /> Ad Performance
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              Sponsored Products, Brands &amp; Display — spend, sales, ACOS &amp; keyword insights
            </p>
          </div>
          <div className="flex items-center gap-2">
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />
            <select value={datePreset} onChange={(e) => setDatePreset(e.target.value)} className={selectCls} title="Date range">
              {DATE_PRESETS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2.5 rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-50"
              title="Refresh"
            >
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
            </button>
            <button
              onClick={() => setAdsModalOpen(true)}
              disabled={sellerChannels.length === 0}
              title={sellerChannels.length === 0 ? 'Connect a marketplace first' : 'Connect Amazon Ads account'}
              className="inline-flex items-center px-3 py-2 border border-[#f08804] text-[#C45500] rounded-lg text-sm font-medium hover:bg-[#FFF4E6] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Link2 size={15} className="mr-1.5" />
              Connect Ads
            </button>
            <button
              onClick={handleSync}
              disabled={syncing || loading || sellerChannels.length === 0}
              title={sellerChannels.length === 0 ? 'No marketplace connected' : 'Sync ad data'}
              className="inline-flex items-center px-4 py-2 bg-[#232F3E] text-white rounded-lg text-sm font-medium hover:bg-[#37475A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {syncing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <RefreshCw size={16} className="mr-2" />}
              {syncing ? 'Syncing...' : 'Sync Ad Data'}
            </button>
            <button
              onClick={exportCsv}
              disabled={filteredRows.length === 0}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download size={16} className="mr-2" />
              Export
            </button>
          </div>
        </div>

        {/* Phase notice */}
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs text-amber-800">
          <Info size={15} className="mt-0.5 shrink-0" />
          <span>
            {isDemo ? (
              <>
                Showing <strong>sample data</strong> for preview. Sponsored Ads data comes from the{' '}
                <strong>Amazon Advertising API</strong> (separate from SP-API); live sync isn’t wired up yet, so these
                figures are placeholders that mirror the real table structure.
              </>
            ) : (
              <>
                Sponsored Ads data comes from the <strong>Amazon Advertising API</strong> (separate from SP-API). This
                view shows synced data from your connected account.
              </>
            )}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {statCards.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon size={20} className="text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Secondary stats strip */}
        {stats && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-500 px-1">
            <span><Eye size={12} className="inline mr-1" />Impressions: <strong className="text-gray-700">{formatNum(stats.impressions)}</strong></span>
            <span><MousePointerClick size={12} className="inline mr-1" />Clicks: <strong className="text-gray-700">{formatNum(stats.clicks)}</strong></span>
            <span>CTR: <strong className="text-gray-700">{formatPct(stats.ctr)}</strong></span>
            <span>Orders: <strong className="text-gray-700">{formatNum(stats.orders)}</strong></span>
            <span>Active campaigns: <strong className="text-gray-700">{formatNum(stats.activeCampaignCount)}</strong> / {formatNum(stats.campaignCount)}</span>
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {[
            { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
            { id: 'keywords', label: 'Keywords & Targets', icon: Target },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.id ? 'border-[#f08804] text-[#C45500]' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <t.icon size={15} /> {t.label}
            </button>
          ))}
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tab === 'campaigns' ? 'Search campaign or ID...' : 'Search keyword, target or campaign...'}
                className="pl-9 pr-8 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-[280px]"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500">
                  <X size={14} />
                </button>
              )}
            </div>

            {tab === 'campaigns' ? (
              <>
                <select value={filterAdType} onChange={(e) => setFilterAdType(e.target.value)} className={selectCls}>
                  <option value="">All Ad Types</option>
                  <option value="SPONSORED_PRODUCTS">Sponsored Products</option>
                  <option value="SPONSORED_BRANDS">Sponsored Brands</option>
                  <option value="SPONSORED_DISPLAY">Sponsored Display</option>
                </select>
                <select value={filterState} onChange={(e) => setFilterState(e.target.value)} className={selectCls}>
                  <option value="">All States</option>
                  <option value="ENABLED">Enabled</option>
                  <option value="PAUSED">Paused</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </>
            ) : (
              <select value={filterTargetType} onChange={(e) => setFilterTargetType(e.target.value)} className={selectCls}>
                <option value="">All Target Types</option>
                <option value="KEYWORD">Keywords</option>
                <option value="PRODUCT_TARGET">Product Targets</option>
                <option value="AUTO_TARGET">Auto Targets</option>
              </select>
            )}

            {hasFilters && (
              <button
                onClick={() => {
                  setSearch('');
                  setFilterAdType('');
                  setFilterState('');
                  setFilterTargetType('');
                }}
                className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800"
              >
                <X size={14} className="mr-1" /> Clear
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {tab === 'campaigns' ? (
                  <tr>
                    <Th onClick={() => handleSort('name')}>Campaign <SortIcon field="name" /></Th>
                    <Th>Type</Th>
                    <Th>State</Th>
                    <Th right onClick={() => handleSort('cost')}>Spend <SortIcon field="cost" /></Th>
                    <Th right onClick={() => handleSort('sales')}>Sales <SortIcon field="sales" /></Th>
                    <Th right onClick={() => handleSort('impressions')}>Impr. <SortIcon field="impressions" /></Th>
                    <Th right onClick={() => handleSort('clicks')}>Clicks <SortIcon field="clicks" /></Th>
                    <Th right onClick={() => handleSort('acos')}>ACOS <SortIcon field="acos" /></Th>
                    <Th right onClick={() => handleSort('roas')}>ROAS <SortIcon field="roas" /></Th>
                  </tr>
                ) : (
                  <tr>
                    <Th onClick={() => handleSort('keywordText')}>Keyword / Target <SortIcon field="keywordText" /></Th>
                    <Th>Match</Th>
                    <Th>Campaign</Th>
                    <Th right onClick={() => handleSort('cost')}>Spend <SortIcon field="cost" /></Th>
                    <Th right onClick={() => handleSort('sales')}>Sales <SortIcon field="sales" /></Th>
                    <Th right onClick={() => handleSort('clicks')}>Clicks <SortIcon field="clicks" /></Th>
                    <Th right onClick={() => handleSort('acos')}>ACOS <SortIcon field="acos" /></Th>
                    <Th right onClick={() => handleSort('roas')}>ROAS <SortIcon field="roas" /></Th>
                  </tr>
                )}
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: tab === 'campaigns' ? 9 : 8 }).map((_, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${50 + ((i + j) % 5) * 10}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={tab === 'campaigns' ? 9 : 8} className="px-6 py-16 text-center">
                      <BarChart3 size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg font-medium">
                        {tab === 'campaigns' ? 'No campaigns yet' : 'No keywords or targets yet'}
                      </p>
                      <p className="text-gray-400 text-sm mt-1 max-w-md mx-auto">
                        {hasFilters
                          ? 'Try adjusting your filters.'
                          : 'Ad data will appear here once the Amazon Advertising API sync is enabled.'}
                      </p>
                    </td>
                  </tr>
                ) : tab === 'campaigns' ? (
                  pageRows.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 max-w-[260px]">
                        <p className="text-sm font-medium text-gray-700 truncate" title={c.name}>{c.name || '—'}</p>
                        <p className="text-[10px] text-gray-400 font-mono">{c.campaignId}</p>
                      </td>
                      <td className="px-4 py-3"><AdTypeBadge adType={c.adType} /></td>
                      <td className="px-4 py-3"><StateBadge state={c.state} /></td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{formatPrice(c.cost)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{formatPrice(c.sales)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{formatNum(c.impressions)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{formatNum(c.clicks)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{formatPct(c.acos)}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">{c.roas != null ? `${Number(c.roas).toFixed(2)}x` : '—'}</td>
                    </tr>
                  ))
                ) : (
                  pageRows.map((k) => (
                    <tr key={k.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 max-w-[240px]">
                        <p className="text-sm font-medium text-gray-700 truncate" title={k.keywordText || k.expression}>
                          {k.keywordText || k.expression || '—'}
                        </p>
                        <p className="text-[10px] text-gray-400">{AD_TYPES_TARGET(k.targetType)}</p>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{k.matchType || '—'}</td>
                      <td className="px-4 py-3 max-w-[180px]">
                        <p className="text-xs text-gray-600 truncate" title={k.campaignName}>{k.campaignName || '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{formatPrice(k.cost)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{formatPrice(k.sales)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{formatNum(k.clicks)}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-700">{formatPct(k.acos)}</td>
                      <td className="px-4 py-3 text-right text-sm font-medium text-gray-800">{k.roas != null ? `${Number(k.roas).toFixed(2)}x` : '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination footer */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-t bg-gray-50">
            <span className="text-sm text-gray-500">
              {totalElements > 0 ? `Showing ${startRecord}-${endRecord} of ${totalElements}` : 'No records'}
            </span>
            <div className="flex items-center gap-3">
              <select
                value={size}
                onChange={(e) => {
                  setSize(parseInt(e.target.value));
                  setPage(0);
                }}
                className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>{s} / page</option>
                ))}
              </select>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-500 px-2">
                  {page + 1} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <AdsConnectModal
        isOpen={adsModalOpen}
        onClose={() => setAdsModalOpen(false)}
        onConnect={() => loadData()}
        sellerAccountId={selectedAccountId || sellerChannels[0]?.id || ''}
      />
    </AdminLayout>
  );
}

// ─── Small table header cell ──────────────────────────────────────────────────

function Th({ children, right, onClick }) {
  return (
    <th
      onClick={onClick}
      className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase ${right ? 'text-right' : 'text-left'} ${
        onClick ? 'cursor-pointer select-none' : ''
      }`}
    >
      <div className={`flex items-center gap-1 ${right ? 'justify-end' : ''}`}>{children}</div>
    </th>
  );
}

function AD_TYPES_TARGET(targetType) {
  switch (targetType) {
    case 'KEYWORD': return 'Keyword';
    case 'PRODUCT_TARGET': return 'Product target';
    case 'AUTO_TARGET': return 'Auto target';
    default: return targetType || '';
  }
}
